// Rhema's links to the other SiliconBlast apps. Every call goes through the Rhema server's gateway,
// which adds the person's linked identity. The gateway passes requests on. The one thing it keeps
// is a Queravel answer, for 15 minutes, encrypted with a key only the asking device holds, so the
// answer survives the app leaving the screen or going to the background.

import type { Api, Query, RawInit } from "../api";

export type LinkedProduct = "queravel" | "malakh" | "penielly";

export const LINKED_PRODUCTS: LinkedProduct[] = ["queravel", "malakh", "penielly"];

export const PRODUCT_NAMES: Record<LinkedProduct, string> = {
  queravel: "Queravel",
  malakh: "Malakh",
  penielly: "Penielly",
};

export type LinkStatus = {
  product: LinkedProduct;
  /** False when the other app cannot be reached or is switched off for this server. */
  available: boolean;
  linked: boolean;
  linked_at: string | null;
  scopes: string[];
};

export type LinksOverview = { enabled: boolean; products: LinkStatus[] };

export type SocialProvider = "google" | "apple" | "microsoft";

export const SOCIAL_PROVIDERS: SocialProvider[] = ["google", "apple", "microsoft"];

export const SOCIAL_NAMES: Record<SocialProvider, string> = {
  google: "Google",
  apple: "Apple",
  microsoft: "Microsoft",
};

/** The ways Rhema can prove an account in another app. */
export type LinkMethod = "password" | SocialProvider;

/** A link in progress. The token is good for `expires_in` seconds. */
export type LinkStart = {
  link_token: string;
  expires_in: number | null;
  /** The other app's name as SBI knows it. */
  app_name: string | null;
  methods: LinkMethod[];
  scopes: string[];
};

export type LinkConfirmed = {
  status: "linked";
  product: LinkedProduct;
  app_name: string | null;
  scopes: string[];
};

/** What each scope lets Rhema do, as part of a sentence. `{app}` is the other app's name. */
const SCOPE_WORDS: Record<string, string> = {
  "penielly.read": "search your photos",
  "penielly.upload": "save photos to {app}",
  "queravel.chat": "ask {app} about messages you pick",
  "queravel.library": "let {app} use your documents",
  "queravel.memory": "let {app} use what it remembers about you",
  "queravel.connectors": "let {app} use your connected apps",
  "malakh.draft": "save email drafts in {app}",
  "malakh.send": "send emails you approve",
};

/**
 * Scopes a link works without, which older links do not have. Queravel's let it use the person's
 * own documents, memory and connected apps when it answers.
 */
export const EXTRA_SCOPES: Partial<Record<LinkedProduct, string[]>> = {
  queravel: ["queravel.library", "queravel.memory", "queravel.connectors"],
};

/** The extra scopes a link is missing, so Settings can ask the person to allow them. */
export function missingExtraScopes(product: LinkedProduct, granted: string[]): string[] {
  return (EXTRA_SCOPES[product] ?? []).filter((s) => !granted.includes(s));
}

/**
 * One plain sentence saying what linking lets Rhema do, such as
 * "Rhema will be able to search your photos and save photos to Penielly."
 */
export function describeScopes(scopes: string[], appName: string): string {
  const parts = scopes.flatMap((s) => {
    const words = SCOPE_WORDS[s];
    return words ? [words.replace("{app}", appName)] : [];
  });
  if (parts.length === 0) return `Rhema will be able to use your ${appName} account.`;
  if (!parts.some((p) => p.includes(appName))) parts[parts.length - 1] += ` in ${appName}`;
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
  return `Rhema will be able to ${list}.`;
}

export type LinkActivity = { product: LinkedProduct; action: string; created_at: string };

export type LinkErrorCode =
  | "links_disabled"
  | "unavailable"
  | "not_linked"
  | "not_found"
  | "method_not_allowed"
  | "refused"
  | "missing_scope"
  | "origin_not_allowed"
  | "too_large"
  | "sign_in_again"
  | "rate_limited"
  | "quota_exceeded"
  | "invalid"
  | "wrong_password"
  | "link_expired"
  | "not_verified"
  | "app_off"
  | "failed";

const MESSAGES: Record<LinkErrorCode, string> = {
  links_disabled: "Connected apps are turned off on this server.",
  unavailable: "That app cannot be reached right now. Try again later.",
  not_linked: "Link your account first.",
  not_found: "That could not be found.",
  method_not_allowed: "Rhema is not allowed to do that.",
  refused: "That app turned the request down.",
  missing_scope: "Your link does not allow this. Unlink the app and link it again.",
  origin_not_allowed: "This version of Rhema cannot do that. Try the web app.",
  too_large: "That is too large to send.",
  sign_in_again: "Sign in to Rhema again to keep using your connected apps.",
  rate_limited: "Too many requests. Wait a moment and try again.",
  quota_exceeded: "You have used your allowance for now. Try again later.",
  invalid: "That request was not accepted.",
  wrong_password: "That email and password did not work.",
  link_expired: "Linking took too long. Start again.",
  not_verified: "Sign in to the other app first.",
  app_off: "An admin turned this app off here.",
  failed: "Something went wrong. Try again.",
};

/** Gateway error codes and the LinkError code each one becomes. */
const GATEWAY_CODES: Record<string, LinkErrorCode> = {
  links_disabled: "links_disabled",
  unavailable: "unavailable",
  sbi_unavailable: "unavailable",
  upstream_unavailable: "unavailable",
  not_linked: "not_linked",
  method_not_allowed: "method_not_allowed",
  missing_scope: "missing_scope",
  origin_not_allowed: "origin_not_allowed",
  bad_request: "invalid",
  too_large: "too_large",
  sign_in_again: "sign_in_again",
  invalid_credentials: "wrong_password",
  invalid_link_token: "link_expired",
  link_expired: "link_expired",
  expired_token: "link_expired",
  not_verified: "not_verified",
  app_off: "app_off",
};

export class LinkError extends Error {
  constructor(
    readonly code: LinkErrorCode,
    readonly status: number,
    readonly product?: LinkedProduct,
    message?: string,
  ) {
    super(message ?? MESSAGES[code]);
  }
}

/** Turns a failed gateway or link response into a LinkError. */
export async function linkErrorFrom(res: Response, product?: LinkedProduct): Promise<LinkError> {
  const data = (await res.json().catch(() => ({}))) as {
    error?: unknown;
    detail?: unknown;
    quota_exceeded?: unknown;
  };
  const code = typeof data.error === "string" ? data.error : "";
  if (data.quota_exceeded === true) return new LinkError("quota_exceeded", res.status, product);
  const known = GATEWAY_CODES[code];
  if (known) return new LinkError(known, res.status, product);
  if (res.status === 404) return new LinkError("not_found", 404, product);
  if (res.status === 410) return new LinkError("link_expired", 410, product);
  if (res.status === 409) return new LinkError("not_linked", 409, product);
  if (res.status === 502 || res.status === 503) return new LinkError("unavailable", res.status, product);
  if (res.status === 429) return new LinkError("rate_limited", 429, product);
  if (res.status === 413) return new LinkError("too_large", 413, product);
  if (res.status === 402) return new LinkError("quota_exceeded", 402, product);
  if (res.status === 400 || res.status === 422) {
    const detail = typeof data.detail === "string" ? data.detail : undefined;
    return new LinkError("invalid", res.status, product, detail);
  }
  if (res.status === 403) {
    // Rhema's own refusals carry an error code. Without one, the other app refused, and its
    // reason (such as "This account has no mailbox on this server.") says more than ours would.
    const detail = typeof data.detail === "string" && data.detail.length <= 200 ? data.detail : undefined;
    return new LinkError("refused", 403, product, detail);
  }
  if (res.status === 401) return new LinkError("method_not_allowed", 401, product);
  return new LinkError("failed", res.status, product);
}

export type GatewayInit = Omit<RawInit, "body"> & { json?: unknown; body?: BodyInit };

/** The Rhema server's link endpoints and its gateway to the other apps. */
export class LinksApi {
  /**
   * `chatId` names the chat these calls are made from, such as a channel someone asks Queravel
   * about. The server checks it against the chat's settings and passes nothing on.
   */
  constructor(
    readonly api: Api,
    readonly chatId?: string,
  ) {}

  /** The same calls, made from a chat. */
  inChat(chatId: string): LinksApi {
    return new LinksApi(this.api, chatId);
  }

  private async json<T>(method: string, path: string, body?: unknown, product?: LinkedProduct): Promise<T> {
    const res = await this.api.raw(method, path, {
      ...(body === undefined
        ? {}
        : { body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }),
    });
    if (!res.ok) throw await linkErrorFrom(res, product);
    if (res.status === 204) return undefined as T;
    return (await res.json().catch(() => ({}))) as T;
  }

  overview(): Promise<LinksOverview> {
    return this.json<LinksOverview>("GET", "/api/links");
  }

  /** Starts linking an account in another app. The answer names the app and what Rhema may do. */
  start(product: LinkedProduct): Promise<LinkStart> {
    return this.json<LinkStart>("POST", `/api/links/${product}/start`, {}, product);
  }

  /** Proves the other account with its email and password. */
  async verifyPassword(
    product: LinkedProduct,
    linkToken: string,
    email: string,
    password: string,
  ): Promise<void> {
    const body = { link_token: linkToken, email, password };
    await this.json("POST", `/api/links/${product}/verify-password`, body, product);
  }

  /**
   * Where to send the browser to prove the other account with Google, Apple or Microsoft. SBI then
   * sends the browser back to /link/callback on the web app.
   */
  socialUrl(product: LinkedProduct, linkToken: string, idp: SocialProvider): string {
    const query = new URLSearchParams({ link_token: linkToken, idp });
    return `${this.api.baseUrl}/api/links/${product}/social?${query.toString()}`;
  }

  /**
   * The SBI address for a social link started in the desktop or mobile app, to open in the system
   * browser. The server remembers that the link belongs to an app, so the web page SBI returns to
   * can hand the browser back on a rhema://link/callback link.
   */
  async nativeSocialUrl(product: LinkedProduct, linkToken: string, idp: SocialProvider): Promise<string> {
    const body = { link_token: linkToken, idp };
    const res = await this.json<{ url?: unknown }>("POST", `/api/links/${product}/social`, body, product);
    if (typeof res.url !== "string" || !/^https:\/\//.test(res.url))
      throw new LinkError("failed", 502, product);
    return res.url;
  }

  /**
   * Asks whether a link token SBI returned belongs to the desktop or mobile app. Gives the product
   * when it does, and null when the link was started in this browser or is unknown.
   */
  async handoff(linkToken: string): Promise<LinkedProduct | null> {
    const res = await this.api.raw("POST", "/api/links/handoff", {
      body: JSON.stringify({ link_token: linkToken }),
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => ({}))) as { product?: unknown };
    return LINKED_PRODUCTS.includes(data.product as LinkedProduct) ? (data.product as LinkedProduct) : null;
  }

  /** Finishes the link once the other account is proved. */
  confirm(product: LinkedProduct, linkToken: string): Promise<LinkConfirmed> {
    return this.json<LinkConfirmed>(
      "POST",
      `/api/links/${product}/confirm`,
      { link_token: linkToken },
      product,
    );
  }

  unlink(product: LinkedProduct): Promise<void> {
    return this.json("DELETE", `/api/links/${product}`, undefined, product);
  }

  activity(): Promise<LinkActivity[]> {
    return this.json<LinkActivity[]>("GET", "/api/links/activity");
  }

  /** Path on the Rhema server for an upstream path in another app. */
  static gatewayPath(product: LinkedProduct, upstream: string): string {
    return `/api/link/${product}${upstream.startsWith("/") ? upstream : `/${upstream}`}`;
  }

  /** Calls another app through the gateway. Throws a LinkError unless the response is ok. */
  async gateway(
    product: LinkedProduct,
    method: string,
    upstream: string,
    init: GatewayInit = {},
  ): Promise<Response> {
    const { json, ...rest } = init;
    const headers = { ...(this.chatId ? { "X-Rhema-Chat": this.chatId } : {}), ...rest.headers };
    const res = await this.api.raw(method, LinksApi.gatewayPath(product, upstream), {
      ...rest,
      headers,
      ...(json === undefined
        ? {}
        : {
            body: JSON.stringify(json),
            headers: { "Content-Type": "application/json", ...headers },
          }),
    });
    if (!res.ok) throw await linkErrorFrom(res, product);
    return res;
  }

  /** A gateway call that returns JSON. */
  async gatewayJson<T>(
    product: LinkedProduct,
    method: string,
    upstream: string,
    init: { json?: unknown; query?: Query; signal?: AbortSignal } = {},
  ): Promise<T> {
    const res = await this.gateway(product, method, upstream, init);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
}

// Activity. The gateway records one row per call. Rows close together are counted as one line.

type ActivityKind =
  | "linked"
  | "unlinked"
  | "searched"
  | "fetched"
  | "saved"
  | "drafted"
  | "emailed"
  | "asked";

function kindOf(row: LinkActivity): ActivityKind | null {
  const a = row.action.toLowerCase();
  if (/\bunlink/.test(a)) return "unlinked";
  if (/\blink/.test(a) && !/link\//.test(a)) return "linked";
  if (a.includes("emailsubmission") || a === "send" || a.endsWith(".send")) return "emailed";
  if (a.includes("email/set") || a.includes("draft")) return "drafted";
  if (a.includes("upload")) return row.product === "malakh" ? "drafted" : "saved";
  if (a.includes("download") || a.includes("original")) return "fetched";
  if (a.includes("search") || a.includes("timeline") || a.includes("album")) return "searched";
  if (a.includes("chat") || a.includes("stream") || a.includes("ask")) return "asked";
  return null;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}` : `${n} ${many}`;
}

function sentence(kind: ActivityKind, product: LinkedProduct, n: number): string {
  const name = PRODUCT_NAMES[product];
  switch (kind) {
    case "linked":
      return `Linked your ${name} account`;
    case "unlinked":
      return `Unlinked your ${name} account`;
    case "searched":
      return n === 1 ? `Searched your photos in ${name}` : `Searched your photos in ${name} ${n} times`;
    case "fetched":
      return `Sent ${plural(n, "photo", "photos")} from ${name} to a chat`;
    case "saved":
      return `Sent ${plural(n, "photo", "photos")} to ${name}`;
    case "drafted":
      return `Saved an email draft in ${name}`;
    case "emailed":
      return `Sent ${plural(n, "email", "emails")} with ${name}`;
    case "asked":
      return n === 1 ? `Asked ${name} a question` : `Asked ${name} ${n} questions`;
  }
}

export type ActivityLine = { product: LinkedProduct; text: string; at: string };

/** Rows within this many milliseconds of each other, for the same app and action, read as one line. */
const GROUP_MS = 2 * 60 * 1000;

/**
 * Turns activity rows into short lines such as "Sent 2 photos to Penielly". Rows come newest first.
 * Several uploads in one go become one line. A draft and its attachments become one line.
 */
export function describeActivity(rows: LinkActivity[]): ActivityLine[] {
  const sorted = [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const lines: (ActivityLine & { kind: ActivityKind; n: number; last: number })[] = [];
  for (const row of sorted) {
    const kind = kindOf(row);
    if (!kind) continue;
    const at = Date.parse(row.created_at);
    const prev = lines[lines.length - 1];
    if (prev && prev.kind === kind && prev.product === row.product && prev.last - at <= GROUP_MS) {
      // A draft counts once however many attachments were uploaded for it.
      if (kind !== "drafted") prev.n += 1;
      prev.last = at;
      prev.text = sentence(kind, row.product, prev.n);
      continue;
    }
    lines.push({
      product: row.product,
      kind,
      n: 1,
      last: at,
      at: row.created_at,
      text: sentence(kind, row.product, 1),
    });
  }
  return lines.map(({ product, text, at }) => ({ product, text, at }));
}
